import React from 'react';
import { useParams } from 'react-router';
import { CardTitle } from 'components/reactstrap';
import { CheckIcon } from 'components/icons';
import { RoutedTabsPages, useRoutedTabs } from 'components/tabs';
import {
  NewHeader,
  classes as headerClasses,
} from 'components/header/NewHeader';
import { NewHeaderActionsProps } from 'components/header/types';
import { RouterPrompt } from 'components/routerPrompt';
import { ensureNonEmptyItems } from 'utils';
import { Container } from 'components/container';
import { AuthContext } from 'auth';
import { JobsiteFeatureFlagsProvider, useFlags } from 'featureFlags';
import { LoadingError } from 'components/loadingError';
import { useSitePermissions } from 'sitePermissions/useSitePermissions';
import { JobsiteConfigTabApi, JobsiteConfigTabProps } from './types';
import { getTabsConfig } from './tabsConfig';

const classes = {
  container: 'odin-h-full',
  header: 'odin-flex-shrink-0',
  tabPage: 'odin-flex-1 odin-overflow-y-auto odin-bg-white',
  headerTitleContainer: 'odin-flex odin-space-x-5 odin-items-center',
  headerTitleBadgesContainer: 'odin-space-x-2',
};

function JobsiteConfigTabs({
  jobsiteId,
}: {
  jobsiteId: string;
}): React.ReactElement {
  const baseRoute = `/jobsite/${jobsiteId}/settings`;

  const [isFormDirty, setIsFormDirty] = React.useState(false);

  const [tabApi, setTabApi] = React.useState<JobsiteConfigTabApi>(null);
  // The shown tab's own save; one left running on another tab doesn't count.
  const isSaving = Boolean(tabApi?.isSaving);
  const submitForm = React.useCallback(
    (): Promise<boolean> => tabApi?.submit?.() ?? Promise.resolve(false),
    [tabApi],
  );

  const { currentUser: user } = React.useContext(AuthContext);
  const { siteLayoutUiEnabled } = useFlags();
  const sitePermissions = useSitePermissions(jobsiteId);
  const effectiveTabsConfig = React.useMemo(
    () =>
      getTabsConfig({
        siteLayoutUiEnabled: Boolean(siteLayoutUiEnabled),
        jobsiteId,
        user,
        sitePermissions,
      }),
    [siteLayoutUiEnabled, jobsiteId, user, sitePermissions],
  );

  const headerActionsProps = React.useMemo(
    (): NewHeaderActionsProps => ({
      menuItems: ensureNonEmptyItems([
        !!tabApi?.submit && {
          onClick: submitForm,
          text: 'Save Changes',
          hideTextOnMobile: false,
          withSpinner: isSaving,
          icon: CheckIcon,
          disabled: !isFormDirty,
        },
      ]),
      baseRoute,
      headerActions: ['back', 'reload'],
      onReloadPressed: tabApi?.refetchData,
    }),
    [submitForm, isFormDirty, isSaving, tabApi],
  );

  const onTabApiChange = React.useCallback(
    ({ jobsiteName, ...currentTabApi }: Partial<JobsiteConfigTabApi>) =>
      setTabApi((prev) => ({
        ...prev,
        ...currentTabApi,
        // update jobsiteName only if it's provided
        ...(jobsiteName ? { jobsiteName } : undefined),
      })),
    [],
  );

  const tabsPageProps: JobsiteConfigTabProps = {
    jobsiteId,
    onIsDirtyChange: setIsFormDirty,
    onTabApiChange,
  };

  const headerTitle = (
    <div className={classes.headerTitleContainer}>
      <CardTitle as="h1" className={headerClasses.title}>
        {tabApi?.jobsiteName}
      </CardTitle>
    </div>
  );

  const { tabs, currentTab } = useRoutedTabs({
    tabsConfig: effectiveTabsConfig,
    baseRoute,
    // loading: networkStatus === NetworkStatus.loading,
  });

  // No tab yet while the site's permissions load, so a link to a zones tab
  // is not sent to the first legacy one meanwhile; and none at all for a
  // user who holds no tab here. A direct URL lands on this, never on a tab.
  if (sitePermissions.loading || !effectiveTabsConfig.length)
    return (
      <Container className={classes.container}>
        {sitePermissions.loading ? (
          <LoadingError loading />
        ) : (
          <p className="odin-p-6 odin-text-sm odin-text-gray-500">
            You do not have access to this site&apos;s settings.
          </p>
        )}
      </Container>
    );

  return (
    <Container className={classes.container}>
      <RouterPrompt
        when={isFormDirty}
        onConfirm={submitForm}
        isSaving={isSaving}
      />
      <NewHeader
        className={classes.header}
        title={headerTitle}
        tabsProps={{ tabs, currentTab }}
        actionsProps={headerActionsProps}
      />
      <RoutedTabsPages
        className={classes.tabPage}
        baseRoute={baseRoute}
        tabs={tabs}
        componentProps={tabsPageProps}
      />
    </Container>
  );
}

/** Jobsite settings, with the site's own feature flags in scope for its tabs. */
export function JobsiteConfigContainer(): React.ReactElement {
  const { jobsiteId } = useParams<{ jobsiteId: string }>();
  return (
    // Keyed by site, so one site's flags never decide another site's tabs
    // while the next site's flags load.
    <JobsiteFeatureFlagsProvider key={jobsiteId} jobsiteId={jobsiteId}>
      <JobsiteConfigTabs jobsiteId={jobsiteId} />
    </JobsiteFeatureFlagsProvider>
  );
}
